import { createContext, useContext, useMemo, useState } from "react";
import { translations } from "./index";

const LanguageContext = createContext(null);
const STORAGE_KEY = "cymlex_language"

// Looks up a dot-path ("resultsBox.tabs.overview") inside a nested
// translations object. Returns undefined if any part of the path is
// missing, rather than throwing.
function resolvePath(dict, path) {
    return path.split(".").reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
        dict
    );
}

// Replaces {placeholders} in a string with values from `vars`.
// Leaves non-string values (arrays, objects) untouched.
function interpolate(value, vars) {
    if (typeof value !== "string" || !vars) return value;
    return value.replace(/\{(\w+)\}/g, (match, key) =>
        vars[key] !== undefined ? vars[key] : match
    );
}

// Wrap the app (or router) with this once, near the root, so every
// component can call useTranslation() to read/change the language.
//
//   <LanguageProvider>
//     <App />
//   </LanguageProvider>
export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem(STORAGE_KEY) || "welsh"; // default
    });

    function switchLanguage(lang) {
        setLanguage(lang);
        localStorage.setItem(STORAGE_KEY, lang);
    }

    const t = useMemo(() => {
        const dict = language === "english" ? translations.en : translations.cy;

        return (path, vars) => {
            const value = resolvePath(dict, path);
            if (value !== undefined) return interpolate(value, vars);

            // Missing in the active dictionary (likely an untranslated
            // Welsh string) — fall back to English so the UI never shows
            // a raw key, then finally to the key itself as a last resort
            // so missing strings are still obvious to a developer.
            const fallback = resolvePath(translations.en, path);
            return interpolate(fallback !== undefined ? fallback : path, vars);
        };
    }, [language]);

    const value = { language, switchLanguage, t };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useTranslation() {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        throw new Error("useTranslation() must be used inside a <LanguageProvider>");
    }
    return ctx;
}
